import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { StatCard } from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Plus, LandPlot, DollarSign, CheckCircle, BarChart3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { InvestmentFormDialog } from "@/components/investments/InvestmentFormDialog";
import { InvestmentCard } from "@/components/investments/InvestmentCard";
import { OfferLetterDialog } from "@/components/investments/OfferLetterDialog";
import { emptyForm, NUM_FIELDS, fmtEur } from "@/components/investments/investmentUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvestmentOpportunitiesPage({ user, onLogout }) {
  const { t } = useTranslation();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [generatingOffer, setGeneratingOffer] = useState(null);
  const [offerText, setOfferText] = useState(null);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchInvestments = async () => {
    try {
      const { data } = await axios.get(`${API}/investments`, { headers });
      setInvestments(data);
    } catch (e) {
      toast.error("Failed to load investments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvestments(); }, []);

  const preparePayload = (f) => {
    const payload = { ...f };
    NUM_FIELDS.forEach(k => { payload[k] = parseFloat(payload[k]) || 0; });
    return payload;
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/investments`, preparePayload(form), { headers });
      toast.success("Investment created");
      setShowCreate(false);
      setForm({ ...emptyForm });
      fetchInvestments();
    } catch { toast.error("Failed to create investment"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const payload = preparePayload(form);
      ["id","financials","ai_analysis","created_by","created_at","updated_at","analyzed_at","sale_price_per_m2","commercial_net_m2"].forEach(k => delete payload[k]);
      await axios.patch(`${API}/investments/${editInv.id}`, payload, { headers });
      toast.success("Investment updated");
      setEditInv(null);
      fetchInvestments();
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this investment opportunity?")) return;
    try {
      await axios.delete(`${API}/investments/${id}`, { headers });
      toast.success("Deleted");
      fetchInvestments();
    } catch { toast.error("Failed to delete"); }
  };

  const handleAnalyze = async (id) => {
    setAnalyzing(id);
    try {
      await axios.post(`${API}/investments/${id}/analyze`, {}, { headers });
      toast.success("AI analysis complete");
      fetchInvestments();
    } catch { toast.error("AI analysis failed"); }
    finally { setAnalyzing(null); }
  };

  const handleGenerateOffer = async (id) => {
    setGeneratingOffer(id);
    try {
      const { data } = await axios.post(`${API}/investments/${id}/generate-offer`, {}, { headers });
      setOfferText(data.offer);
      toast.success("Offer letter generated");
    } catch { toast.error("Failed to generate offer"); }
    finally { setGeneratingOffer(null); }
  };

  const openEdit = (inv) => {
    setForm({
      name: inv.name || "", location: inv.location || "", description: inv.description || "",
      total_land_area_m2: String(inv.total_land_area_m2 || ""),
      gross_buildable_m2: String(inv.gross_buildable_m2 || ""),
      deduction_percentage: String(inv.deduction_percentage || ""),
      commercial_gross_m2: String(inv.commercial_gross_m2 || ""),
      commercial_deduction_percentage: String(inv.commercial_deduction_percentage || ""),
      deal_type: inv.deal_type || "cash",
      land_price: String(inv.land_price || ""),
      owner_percentage: String(inv.owner_percentage || ""),
      cash_portion: String(inv.cash_portion || ""),
      communal_tax_per_m2: String(inv.communal_tax_per_m2 || ""),
      construction_cost_per_m2: String(inv.construction_cost_per_m2 || ""),
      residential_sale_price_per_m2: String(inv.residential_sale_price_per_m2 || inv.sale_price_per_m2 || ""),
      commercial_sale_price_per_m2: String(inv.commercial_sale_price_per_m2 || ""),
      notes: inv.notes || "", status: inv.status || "new",
    });
    setEditInv(inv);
  };

  const summary = useMemo(() => {
    const total = investments.length;
    const profitable = investments.filter(i => (i.financials?.profit || 0) > 0).length;
    const totalProfit = investments.reduce((s, i) => s + (i.financials?.profit || 0), 0);
    const avgMargin = investments.length > 0 ? investments.reduce((s, i) => s + (i.financials?.profit_margin || 0), 0) / investments.length : 0;
    return { total, profitable, totalProfit, avgMargin };
  }, [investments]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="investment-opportunities-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">{t("investments.title", "Investment Opportunities")}</h1>
            <p className="text-slate-500 text-sm mt-1">{t("investments.subtitle", "Analyze and compare land development opportunities")}</p>
          </div>
          <Button onClick={() => { setForm({ ...emptyForm }); setShowCreate(true); }} data-testid="add-investment-btn">
            <Plus className="w-4 h-4 mr-2" />{t("investments.add", "New Opportunity")}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={LandPlot} label={t("investments.totalOpportunities", "Opportunities")} value={summary.total} color="blue" />
          <StatCard icon={CheckCircle} label={t("investments.profitable", "Profitable")} value={summary.profitable} color="green" />
          <StatCard icon={DollarSign} label={t("investments.totalProfit", "Total Potential Profit")} value={fmtEur(summary.totalProfit)} color="emerald" />
          <StatCard icon={BarChart3} label={t("investments.avgMargin", "Avg Margin")} value={`${summary.avgMargin.toFixed(1)}%`} color="violet" />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : investments.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <LandPlot className="w-12 h-12 text-slate-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-600">No investment opportunities yet</h3>
              <p className="text-sm text-slate-400 mt-1 max-w-md">Add your first land or building opportunity to start analyzing potential returns.</p>
              <Button className="mt-4" onClick={() => { setForm({ ...emptyForm }); setShowCreate(true); }} data-testid="empty-add-btn">
                <Plus className="w-4 h-4 mr-2" />Add Opportunity
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {investments.map(inv => (
              <InvestmentCard key={inv.id} inv={inv} onEdit={openEdit} onDelete={handleDelete} onAnalyze={handleAnalyze} onGenerateOffer={handleGenerateOffer} analyzing={analyzing} generatingOffer={generatingOffer} />
            ))}
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <InvestmentFormDialog form={form} setForm={setForm} onSave={handleCreate} onClose={() => setShowCreate(false)} saving={saving} title={t("investments.create", "New Investment Opportunity")} />
        </Dialog>

        <Dialog open={!!editInv} onOpenChange={(o) => { if (!o) setEditInv(null); }}>
          <InvestmentFormDialog form={form} setForm={setForm} onSave={handleUpdate} onClose={() => setEditInv(null)} saving={saving} title={t("investments.edit", "Edit Investment Opportunity")} />
        </Dialog>

        <OfferLetterDialog offerText={offerText} onClose={() => setOfferText(null)} />
      </div>
    </Layout>
  );
}
